import React from 'react';
import { connect } from 'react-redux';

import {
  Card,
  CardContent,
  Button,
  FormControl,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  setPasswordFieldShouldReset,
  submitLoginForm,
  validateResetPassHash,
  isForcePasswordActiveForTheUser,
} from '../reducers/UserReducer';
import { checkForUpdates } from '../reducers/AppInfoReducer';
import splashScreen from '../assets/images/login-splash.jpg';
import IconRms from '../assets/images/icon-RMS.svg';
import ptsLogo from '../assets/images/PTS-logo.png';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';

import { notify } from 'reducers/NotifierReducer';
import { sendPassResetLink, setSecondFactor } from '../reducers/UserReducer';
import { useHistory } from 'react-router-dom';
import { validateEmail } from 'utils/functions';

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  card: {
    width: 480,
    maxWidth: '95%',
    background: 'fff',
    '& img': {
      width: '100%',
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  branding: {
    width: '100%',
    height: 200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#0051a0',
    '& img': {
      width: 150,
      height: 'auto',
    },
  },
  branding2: {
    width: '100%',
    textAlign: 'center',
    padding: '25px 0 5px',
    '& img': {
      height: 50,
      width: 'auto',
    },
  },
  actions: {
    textAlign: 'center',
    justifyContent: 'center',
    marginTop: 30,
    '& .customBtn': {
      background: '#0051a0',
      color: '#fff',
      '&:hover': {
        background: '#021090',
      },
    },
    '& button': {
      paddingLeft: '4em',
      paddingRight: '4em',
      textTransform: 'uppercase',
    },
    '& button.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  passReset: {
    textAlign: 'right',
    '& span': {
      fontSize: 13,
      color: '#808080',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  version: {
    position: 'absolute',
    bottom: '0.5em',
    left: '1em',
    lineHeight: 1,
  },
  passResetDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  codeText: {
    color: '#0153a3',
    marginBottom: '2px',
    display: 'block',
  },
}));

const PageLogin = (props) => {
  const { themeMode, checkForUpdates, secondFactor, setSecondFactor } = props;
  const [secondFactorToken, setSecondFactorToken] = React.useState('');
  const [code, setCode] = React.useState(null);
  const [forcePasswordReset, setForcePasswordReset] = React.useState(false);
  const history = useHistory();
  const { appVersion } = props.appInfo;
  const classes = useStyles();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [view, setView] = React.useState('login'); // login, pass-reset
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    props.checkForUpdates();
  }, []);

  React.useEffect(() => {
    if (props.passwordFieldShouldReset) {
      setPassword('');
    }
    props.setPasswordFieldShouldReset(false);
  }, [props.passwordFieldShouldReset]);

  React.useEffect(() => {
    setSecondFactor(false);
  }, []);

  React.useEffect(() => {
    if (secondFactor === true) setView('login-second-factor');
    if (secondFactor === false) setView('login');
  }, [secondFactor]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleCodeChange = (event) => setCode(event.target.value);

  const clickCancel = () => {
    setCode(null);
    setUsername('');
    setView('login');
    setSecondFactor(false);
    setForcePasswordReset(false);
  };

  const handleSecondFactorTokenChange = (event) => {
    setSecondFactorToken(event.target.value);
  };
  const handleResetEmailChange = (event) => {
    setEmail(event.target.value.toLowerCase());
  };

  const submitForm = async event => {
    event.preventDefault();
    const forcePasswordReset = await isForcePasswordActiveForTheUser(username);
    if (!forcePasswordReset) {
      props.submitLoginForm(username, password);
    } else {
      setForcePasswordReset(true);
      await passResetClick(false);
    }
  };

  const passResetSubmit = async (event) => {
    event.preventDefault();
    try {
      const valid = await validateResetPassHash(code, username);
      if (valid.status === 'expired') {
        props.notify('This code has expired. Please click resend code for a new code');
      } else if (valid.status === 'invalid') {
        props.notify('The code you entered is invalid. Please check code and try again');
      } else if (valid.status === 'valid') {
        history.push('/Reset-password', { user: username, code });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const secondFactorSubmit = async (event) => {
    event.preventDefault();
    props.submitLoginForm(username, password, false, secondFactorToken);
  };

  const passResetClick = async (isResendRequest = false) => {
    try {
      const response = await sendPassResetLink(username, isResendRequest);
      if (!response || response === 'disabled') {
        setView('login');
        props.notify(
          response === 'disabled'
            ? 'User is disabled. Please contact system administrator.'
            : 'Username does not exist. Please enter a valid username.',
          response === 'disabled' ? 'error' : 'info'
        );
      } else {
        if (!isResendRequest) {
          setEmail(response);
          setView('pass-reset');
        } else {
          props.notify(
            'Your verification code has been resent. Please check your email.',
            'success'
          );
        }
      }
    } catch (err) {
      console.log(err);
      props.notify('Error sending password reset Code', 'error');
    }
  };

  const renderPassReset = () => (
    <form onSubmit={passResetSubmit}>
      <div className="mb-3">
        <FormControl size="small" className="w-100">
          <TextField
            required
            variant="outlined"
            label="Username"
            placeholder="Enter your username"
            type="text"
            size="small"
            disabled={true}
            value={username}
            onChange={handleUsernameChange}
            className={classes.textField}
          />
        </FormControl>
      </div>
      {!code && (
        <span className={`${classes.codeText} px-1 d`}>Check email for code sent to {email}</span>
      )}

      <div className={`${classes.formWrap} mt-2 mb-2`}>
        <FormControl size="small" className="w-100">
          <TextField
            type="number"
            required
            variant="outlined"
            label="Code"
            size="small"
            placeholder="Enter code here"
            value={code}
            onChange={handleCodeChange}
            className={classes.textField}
          />
        </FormControl>
      </div>
      <div className={classes.passReset}>
        <span onClick={() => passResetClick(true)}>Resend code</span>
      </div>

      <div className={classes.actions}>
        <div className={`d-flex justify-content-between ${classes.content}`}>
          <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={!code}
            className="customBtn">
            Continue
          </Button>
        </div>
      </div>
    </form>
  );

  const renderSecondFactor = () => {
    return (
      <form onSubmit={secondFactorSubmit}>
        <div className="mb-3">
          <FormControl size="small" className="w-100">
            <TextField
              required
              variant="outlined"
              label="Token"
              fullWidth
              placeholder="Enter token"
              type="text"
              size="small"
              value={secondFactorToken}
              onChange={handleSecondFactorTokenChange}
              InputProps={{
                className: classes.input,
              }}
            />
          </FormControl>
        </div>
        <div className={classes.actions}>
          <div className={`d-flex justify-content-between ${classes.content}`}>
            <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              className="customBtn">
              Login
            </Button>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        <div className={classes.branding}>
          <img src={IconRms} alt="MOBILE icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        {forcePasswordReset && view === 'pass-reset' && (
          <div style={{ color: 'red', textAlign: 'center', marginTop: '7px' }}>
            For security reasons, you must change your password.
          </div>
        )}
        <CardContent>
          {view === 'login' && (
            <form onSubmit={submitForm}>
              <div className="mb-3">
                <FormControl size="small" className="w-100">
                  <TextField
                    autoComplete="hidden"
                    size="small"
                    required
                    variant="outlined"
                    label="Username"
                    fullWidth
                    placeholder="Enter your username"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                  />
                </FormControl>
              </div>
              <div className="mb-3">
                <FormControl size="small" className="w-100">
                  <TextField
                    inputRef={(input) => props.passwordFieldShouldReset === true && input?.focus()}
                    size="small"
                    required
                    variant="outlined"
                    label="Password"
                    fullWidth
                    placeholder="Enter your password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </div>

              <div className={classes.passReset}>
                <span
                  className={!username ? classes.passResetDisabled : ''}
                  onClick={() => passResetClick(false)}>
                  Password reset
                </span>
              </div>

              <div className={classes.actions}>
                <Button type="submit" variant="contained" size="large" className="customBtn my2">
                  Login
                </Button>
              </div>
            </form>
          )}
          {view === 'login-second-factor' && renderSecondFactor()}
          {view === 'pass-reset' && renderPassReset()}

          <div className={classes.version}> v. {appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  passwordFieldShouldReset: state.user.passwordFieldShouldReset,
  secondFactor: state.user.secondFactor,
});

export default connect(mapStateToProps, {
  submitLoginForm,
  checkForUpdates,
  notify,
  setPasswordFieldShouldReset,
  setSecondFactor,
  isForcePasswordActiveForTheUser,
})(PageLogin);
